import { ErrorState, Loading, useToast } from "@uzverse/ui";
import { useTranslation } from "react-i18next";
import { useSession } from "../state/session.js";
import { useLoad } from "../state/useLoad.js";
import { useCommonT, useErrorText } from "./common.js";

/** "Chrome on macOS" from a user agent; the raw string is never shown. */
export function describeUserAgent(ua: string | null): string | null {
  if (!ua) return null;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\/|Opera/.test(ua)
      ? "Opera"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Chrome\//.test(ua)
          ? "Chrome"
          : /Safari\//.test(ua)
            ? "Safari"
            : null;
  const os = /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Mac OS X|Macintosh/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : null;
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os;
}

/** Settings → Oturumlar: signed-in browsers (revocable) and trusted devices (revocable). */
export function SessionsSettings() {
  const { t, i18n } = useTranslation("web");
  const tc = useCommonT();
  const errorText = useErrorText();
  const toast = useToast();
  const { services } = useSession();
  const { api } = services;
  const sessions = useLoad(() => api.listSessions());
  const devices = useLoad(() => api.listDevices());
  const when = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));

  async function revokeSession(id: string) {
    try {
      await api.revokeSession(id);
      toast.show(t("settings.sessions.revoked"));
      sessions.reload();
    } catch (err) {
      toast.show(errorText(err));
    }
  }
  async function revokeDevice(id: string) {
    try {
      await api.revokeDevice(id);
      toast.show(t("settings.sessions.deviceRemoved"));
      devices.reload();
    } catch (err) {
      toast.show(errorText(err));
    }
  }

  return (
    <section className="card stack" aria-labelledby="sessions-title">
      <h2 id="sessions-title">{t("settings.sessions.title")}</h2>
      <p className="muted small">{t("settings.sessions.intro")}</p>
      {sessions.data ? (
        <ul className="stack" aria-label={t("settings.sessions.title")}>
          {sessions.data.map((s) => (
            <li key={s.id} className="row row-between" data-testid="session-row">
              <span>
                <strong>
                  {describeUserAgent(s.userAgent) ?? t("settings.sessions.unknownDevice")}
                </strong>
                {s.current && <span className="badge"> {t("settings.sessions.current")}</span>}
                <small className="muted block">
                  {t("settings.sessions.lastSeen", { when: when(s.lastSeenAt) })}
                  {s.ip ? ` · ${s.ip}` : ""}
                </small>
                <small className="muted block">
                  {s.remember
                    ? t("settings.sessions.remembered")
                    : t("settings.sessions.notRemembered")}
                </small>
              </span>
              {!s.current && (
                <button type="button" className="btn" onClick={() => void revokeSession(s.id)}>
                  {t("settings.sessions.revoke")}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : sessions.error ? (
        <ErrorState t={tc} message={errorText(sessions.error)} onRetry={sessions.reload} />
      ) : (
        <Loading t={tc} />
      )}
      <h3>{t("settings.sessions.devicesTitle")}</h3>
      <p className="muted small">{t("settings.sessions.devicesIntro")}</p>
      {devices.data ? (
        devices.data.length === 0 ? (
          <p className="muted">{t("settings.sessions.noDevices")}</p>
        ) : (
          <ul className="stack" aria-label={t("settings.sessions.devicesTitle")}>
            {devices.data.map((d) => (
              <li key={d.id} className="row row-between" data-testid="device-row">
                <span>
                  <strong>
                    {describeUserAgent(d.userAgent) ?? t("settings.sessions.unknownDevice")}
                  </strong>
                  {d.current && <span className="badge"> {t("settings.sessions.current")}</span>}
                  <small className="muted block">
                    {t("settings.sessions.trustedUntil", { when: date(d.expiresAt) })}
                  </small>
                </span>
                <button type="button" className="btn" onClick={() => void revokeDevice(d.id)}>
                  {t("settings.sessions.removeDevice")}
                </button>
              </li>
            ))}
          </ul>
        )
      ) : devices.error ? (
        <ErrorState t={tc} message={errorText(devices.error)} onRetry={devices.reload} />
      ) : (
        <Loading t={tc} />
      )}
    </section>
  );
}
